import { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
} from '../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from '../components/ui/table';
import {
  useGetAllReports,
  type GetAllReportsParams,
} from '@edusense/report-client';
import {
  REPORT_STATUS_VALUES,
  REPORT_STATUS_LABELS,
  REPORT_STATUS_COLOR_MAP,
  REPORT_PRIORITY_VALUES,
  REPORT_PRIORITY_LABELS,
  REPORT_PRIORITY_COLOR_MAP,
  REPORT_TARGET_TYPE_VALUES,
  REPORT_TARGET_TYPE_LABELS,
  REPORT_REASON_VALUES,
  REPORT_REASON_LABELS,
  type ReportFilters,
  formatKoreanDate,
} from '../types/report';

export default function ReportsPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<ReportFilters>({});

  const queryParams = useMemo<GetAllReportsParams>(() => {
    const params: GetAllReportsParams = {
      limit: 50,
      offset: 0,
    };
    if (filters.status) params.status = filters.status;
    if (filters.priority) params.priority = filters.priority;
    if (filters.targetType) params.targetType = filters.targetType;
    if (filters.reason) params.reason = filters.reason;
    return params;
  }, [filters]);

  const {
    data: reports,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetAllReports(queryParams, {
    query: {
      keepPreviousData: true,
    },
  });

  const handleSelection = <T extends keyof ReportFilters>(key: T) => (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const value = e.target.value;
    setFilters((prev) => ({
      ...prev,
      [key]: value === "" ? undefined : value,
    }));
  };

  // Helper mapping HeroUI colors to our new Badge variants
  const getBadgeVariant = (color: string) => {
    switch (color) {
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'danger': return 'destructive';
      case 'primary': return 'default';
      default: return 'secondary';
    }
  };

  const selectClasses = "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

  return (
    <>
      <h1 className="text-3xl font-bold mb-6">신고 관리</h1>

      {/* 필터 */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">상태</label>
              <select
                className={selectClasses}
                value={filters.status || ""}
                onChange={handleSelection('status')}
              >
                <option value="">모든 상태</option>
                {REPORT_STATUS_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {REPORT_STATUS_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">우선순위</label>
              <select
                className={selectClasses}
                value={filters.priority || ""}
                onChange={handleSelection('priority')}
              >
                <option value="">모든 우선순위</option>
                {REPORT_PRIORITY_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {REPORT_PRIORITY_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">대상 타입</label>
              <select
                className={selectClasses}
                value={filters.targetType || ""}
                onChange={handleSelection('targetType')}
              >
                <option value="">모든 타입</option>
                {REPORT_TARGET_TYPE_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {REPORT_TARGET_TYPE_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">신고 사유</label>
              <select
                className={selectClasses}
                value={filters.reason || ""}
                onChange={handleSelection('reason')}
              >
                <option value="">모든 사유</option>
                {REPORT_REASON_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {REPORT_REASON_LABELS[value]}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 테이블 */}
      <Card>
        <CardContent className="pt-6">
          <Table aria-label="신고 목록">
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>대상 타입</TableHead>
                <TableHead>신고 사유</TableHead>
                <TableHead>상태</TableHead>
                <TableHead>우선순위</TableHead>
                <TableHead>생성일</TableHead>
                <TableHead>액션</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="flex items-center justify-center py-6 text-muted-foreground">
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> 신고 데이터를 불러오는 중입니다...
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {isError && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="flex items-center justify-between rounded-lg border border-destructive px-4 py-3 text-destructive">
                      <div>
                        <p className="font-semibold">신고 데이터를 불러오지 못했습니다.</p>
                        <p className="text-sm opacity-80">{error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'}</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => refetch()}>재시도</Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {(!isLoading && !isError && (!reports || reports.length === 0)) && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <div className="text-center py-6 text-muted-foreground">표시할 신고가 없습니다.</div>
                  </TableCell>
                </TableRow>
              )}

              {!isLoading && !isError && reports?.map((report) => (
                <TableRow key={report.id}>
                  <TableCell>{report.id}</TableCell>
                  <TableCell>{REPORT_TARGET_TYPE_LABELS[report.targetType]}</TableCell>
                  <TableCell>
                    <span title={report.description ?? undefined} className="cursor-help cursor-pointer">
                      {REPORT_REASON_LABELS[report.reason]}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getBadgeVariant(REPORT_STATUS_COLOR_MAP[report.status])}>
                      {REPORT_STATUS_LABELS[report.status]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={getBadgeVariant(REPORT_PRIORITY_COLOR_MAP[report.priority])}>
                      {REPORT_PRIORITY_LABELS[report.priority]}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatKoreanDate(report.createdAt)}</TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-primary hover:text-primary/80"
                      onClick={() => navigate(`/reports/${report.id}`)}
                    >
                      상세 보기
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </>
  );
}
